import { Fragment, useRef, useState, useEffect } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XIcon, RefreshIcon } from '@heroicons/react/outline'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import { Pagination } from 'antd'
import { Loader } from '../../../components/Loader'
import { fetchShippingMethodHistory } from '../../../services'
import { ACTION_STYLES, formatDate as formatDateUtil, getChangedFields as getChangedFieldsUtil } from '../utils'

const HISTORY_FIELDS = [
  'is_active',
  'non_working_days',
  'extended_zone',
  'po_box_dropoff',
  'provides_pod',
  'enable_multipackage',
  'carrier_name',
  'shipping_name',
]

// A coverage batch can carry thousands of changes; render this many until the user asks for all.
const COLLAPSED_CHANGES = 50

export const ShippingMethodHistoryModal = ({ open, setOpen, methodData = null, warehouseTimeZone = null }) => {
  const { i18n } = useTranslation()
  const cancelButtonRef = useRef(null)
  const [page, setPage] = useState(1)
  const [expandedIds, setExpandedIds] = useState(() => new Set())

  useEffect(() => {
    setPage(1)
    setExpandedIds(new Set())
  }, [methodData?.id])

  const { data, isLoading, isFetching, refetch } = useQuery(
    ['shipping_method_history', methodData?.id, page],
    () => fetchShippingMethodHistory(methodData.id, { page, per_page: 10 }),
    {
      enabled: open && !!methodData?.id,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    },
  )

  const histories = data?.shipping_method_histories || []
  const meta = data?.meta || {}

  const getActionLabel = action => {
    return i18n.t(`lastmile_management.shipping_methods.history.action_${action}`)
  }

  const getFieldLabel = field => {
    return i18n.t(`lastmile_management.shipping_methods.history.field_${field}`)
  }

  const formatValue = (value, field) => {
    if (value === null || value === undefined) return '-'
    if (field === 'non_working_days' && Array.isArray(value)) {
      if (value.length === 0) return '-'
      return value.map(d => i18n.t(`lastmile_management.days_short.${d}`)).join(', ')
    }
    if (value === true) return i18n.t('lastmile_management.boolean_yes')
    if (value === false) return i18n.t('lastmile_management.boolean_no')
    return String(value)
  }

  const getChangedFields = entry => getChangedFieldsUtil(entry, HISTORY_FIELDS)

  const formatDate = dateStr => formatDateUtil(dateStr, i18n.language, warehouseTimeZone)

  const toggleExpanded = id =>
    setExpandedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const handleClose = () => {
    setOpen(false)
    setPage(1)
    setExpandedIds(new Set())
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" initialFocus={cancelButtonRef} onClose={handleClose}>
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-visible shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                  {i18n.t('lastmile_management.shipping_methods.history.title')}
                  {methodData?.shipping_name ? ` - ${methodData.shipping_name}` : ''}
                </Dialog.Title>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    className="text-gray-400 hover:text-blue-600 transition-colors"
                    onClick={() => refetch()}
                    disabled={isFetching}
                    title={i18n.t('lastmile_management.shipping_methods.history.refresh')}
                  >
                    <RefreshIcon className={`h-5 w-5 ${isFetching ? 'animate-spin' : ''}`} />
                  </button>
                  <button type="button" className="text-gray-400 hover:text-gray-500" onClick={handleClose} ref={cancelButtonRef}>
                    <XIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="relative min-h-[200px]">
                {isLoading ? (
                  <div className="flex justify-center py-12">
                    <Loader show={true} />
                  </div>
                ) : histories.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">{i18n.t('lastmile_management.shipping_methods.history.no_history')}</p>
                  </div>
                ) : (
                  <>
                    {isFetching && <div className="absolute inset-0 bg-white bg-opacity-50 z-10" />}
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              {i18n.t('lastmile_management.shipping_methods.history.action')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              {i18n.t('lastmile_management.shipping_methods.history.changes')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              {i18n.t('lastmile_management.shipping_methods.history.user')}
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                              {i18n.t('lastmile_management.shipping_methods.history.date')}
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {histories.map(entry => (
                            <tr key={entry.id}>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <span
                                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${ACTION_STYLES[entry.action] || 'bg-gray-100 text-gray-800'}`}
                                >
                                  {getActionLabel(entry.action)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <div className="space-y-1 text-sm">
                                  {Array.isArray(entry.after_change?.changes)
                                    ? (() => {
                                        const all = entry.after_change.changes
                                        const total = Number.isFinite(entry.after_change.changes_total) ? entry.after_change.changes_total : all.length
                                        const expanded = expandedIds.has(entry.id)
                                        const visible = expanded ? all : all.slice(0, COLLAPSED_CHANGES)
                                        return (
                                          <>
                                            {visible.map((c, i) => (
                                              <div key={`${c.key}-${c.field}-${i}`} data-testid="history-change" className="flex items-center gap-1 flex-wrap">
                                                <span className="font-medium text-gray-700">
                                                  {c.key} · {getFieldLabel(c.field)}:
                                                </span>
                                                {c.from != null && (
                                                  <>
                                                    <span className="text-red-500 line-through">{formatValue(c.from, c.field)}</span>
                                                    <span className="text-gray-400">&rarr;</span>
                                                  </>
                                                )}
                                                <span className="text-green-600">{formatValue(c.to, c.field)}</span>
                                              </div>
                                            ))}
                                            {all.length > COLLAPSED_CHANGES && (
                                              <button
                                                type="button"
                                                className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                                                onClick={() => toggleExpanded(entry.id)}
                                              >
                                                {expanded
                                                  ? i18n.t('lastmile_management.shipping_methods.history.show_less')
                                                  : i18n.t('lastmile_management.shipping_methods.history.show_all', { count: total })}
                                              </button>
                                            )}
                                            {expanded && total > all.length && (
                                              <p className="text-xs text-gray-500">
                                                {i18n.t('lastmile_management.shipping_methods.history.showing_of_total', { shown: all.length, total })}
                                              </p>
                                            )}
                                          </>
                                        )
                                      })()
                                    : getChangedFields(entry).map(({ field, oldValue, newValue }) => (
                                        <div key={field} className="flex items-center gap-1 flex-wrap">
                                          <span className="font-medium text-gray-700">{getFieldLabel(field)}:</span>
                                          {oldValue != null && (
                                            <>
                                              <span className="text-red-500 line-through">{formatValue(oldValue, field)}</span>
                                              <span className="text-gray-400">&rarr;</span>
                                            </>
                                          )}
                                          <span className="text-green-600">{formatValue(newValue, field)}</span>
                                        </div>
                                      ))}
                                </div>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{entry.created_by?.email || '-'}</td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{formatDate(entry.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {meta.total_pages > 1 && (
                      <div className="flex justify-end mt-4">
                        <Pagination
                          size="small"
                          current={meta.current_page}
                          total={meta.total_count}
                          pageSize={10}
                          onChange={p => setPage(p)}
                          showSizeChanger={false}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
